<template>
  <div
    v-if="open"
    ref="popoverEl"
    class="grid-options-popover"
    :style="popoverMaxHeight ? { maxHeight: popoverMaxHeight } : undefined"
    role="dialog"
    aria-label="Grid options"
    tabindex="-1"
    @keydown.escape="$emit('close')"
  >
    <div ref="scrollEl" class="grid-options-popover__scroll" tabindex="-1" @scroll="updateTooltipPosition">
      <!-- GRID SIZE -->
      <section class="grid-options-popover__section">
        <div class="grid-options-popover__size-layout">
          <h3 class="grid-options-popover__heading grid-options-popover__heading--size">GRID SIZE</h3>
          <div class="grid-options-popover__size">
            <div class="grid-options-popover__size-row">
              <span class="grid-options-popover__size-label">Width</span>
              <input v-model.number="widthFt" type="number" min="0" aria-label="Width in feet" class="grid-options-popover__input"> <span class="grid-options-popover__unit">ft</span>
              <input v-model.number="widthIn" type="number" min="0" max="11" aria-label="Width in inches" class="grid-options-popover__input"> <span class="grid-options-popover__unit">in</span>
            </div>
            <div class="grid-options-popover__size-row">
              <span class="grid-options-popover__size-label">Height</span>
              <input v-model.number="heightFt" type="number" min="0" aria-label="Height in feet" class="grid-options-popover__input"> <span class="grid-options-popover__unit">ft</span>
              <input v-model.number="heightIn" type="number" min="0" max="11" aria-label="Height in inches" class="grid-options-popover__input"> <span class="grid-options-popover__unit">in</span>
            </div>
          </div>
        </div>
      </section>

      <!-- BACKGROUND COLOR -->
      <section class="grid-options-popover__section">
        <div class="grid-options-popover__color-row">
          <h3 class="grid-options-popover__heading">BACKGROUND COLOR</h3>
          <div class="grid-options-popover__color-swatch-wrap">
            <input
              ref="colorInput"
              v-model="bgColor"
              type="text"
              data-coloris
              class="grid-options-popover__color-input"
              :style="{ backgroundColor: bgColor }"
              aria-label="Background color"
              @change="handleBgChange"
            >
          </div>
        </div>
      </section>

      <!-- GRID TYPE -->
      <section class="grid-options-popover__section grid-options-popover__section--types">
        <h3 class="grid-options-popover__heading">GRID TYPE</h3>
        <div class="grid-options-popover__pattern-list">
          <button
            v-for="pattern in orderedPatterns"
            :key="pattern.id"
            type="button"
            class="grid-options-popover__pattern"
            :class="{ 'grid-options-popover__pattern--active': currentPatternId === pattern.id, 'grid-options-popover__pattern--highlighted': highlightedGridIds.includes(pattern.id) }"
            :aria-pressed="currentPatternId === pattern.id"
            @click="selectPattern(pattern)"
            @mouseenter="showTooltip(pattern, $event)"
            @mouseleave="hideTooltip"
          >
            <img :src="PATTERN_ICONS[pattern.id] || pattern.image" :alt="pattern.label" class="grid-options-popover__pattern-img">
            <span class="grid-options-popover__pattern-label">{{ PATTERN_LABELS[pattern.id] || pattern.label }}</span>
            <Transition name="grid-availability">
              <span v-if="highlightedGridIds.includes(pattern.id)" class="grid-options-popover__available">Available</span>
            </Transition>
          </button>
        </div>

      </section>
    </div>
    <VerticalScrollIndicators :target="scrollEl" label="grid options" />
  </div>

  <!-- Floating tooltip outside scrollable popover to prevent clipping -->
  <Teleport to="body">
    <div
      v-if="open && hoveredPattern && PATTERN_INFO[hoveredPattern.id] && tooltipPos"
      class="grid-options-popover__pattern-tooltip"
      :style="{
        top: `${tooltipPos.top}px`,
        left: `${tooltipPos.left}px`
      }"
    >
      {{ PATTERN_INFO[hoveredPattern.id] }}
    </div>
  </Teleport>

  <!-- Grid change warning modal -->
  <Teleport to="body">
    <transition name="fade">
      <div v-if="pendingPattern" class="grid-warning-overlay" @click.self="pendingPattern = null">
        <div class="grid-warning-modal" role="alertdialog" aria-label="Grid change warning">
          <div class="grid-warning-modal__content">
            <p>Changing the grid will reset the Studio. Save any work you want to keep.</p>
            <div class="grid-warning-modal__actions">
              <button type="button" class="grid-warning-modal__cancel" @click="pendingPattern = null">Cancel</button>
              <button type="button" class="grid-warning-modal__confirm" @click="confirmPatternChange">Continue</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { shallowRef } from 'vue'
import { FOOT_IN_PIXELS, GRID_PATTERN_LIST, GRID_PATTERN_IDS, INCH_IN_PIXELS, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { GRID_MENU } from '~/composables/shapes/constants'
import { applyGridPatternChange } from './logic'
import type { GridPattern } from '~/types/grid.types'
import VerticalScrollIndicators from './VerticalScrollIndicators.vue'

const PATTERN_ICONS: Record<string, string> = {
  [GRID_PATTERN_IDS.SQUARE]: '/new-studio/icons/grid-patterns/square.svg',
  [GRID_PATTERN_IDS.SQUARE_45]: '/new-studio/icons/grid-patterns/45-deg.svg',
  [GRID_PATTERN_IDS.HEX]: '/new-studio/icons/grid-patterns/hex.svg',
  [GRID_PATTERN_IDS.HEX_90]: '/new-studio/icons/grid-patterns/hex-90.svg',
  [GRID_PATTERN_IDS.HEX_SPACED]: '/new-studio/icons/grid-patterns/hex-spaced.svg',
  [GRID_PATTERN_IDS.FREEFORM]: '/new-studio/icons/grid-patterns/freeform.svg',
  [GRID_PATTERN_IDS.THREE_D]: '/new-studio/icons/grid-patterns/3d.svg',
}

const PATTERN_LABELS: Record<string, string> = {
  [GRID_PATTERN_IDS.SQUARE]: 'Square',
  [GRID_PATTERN_IDS.SQUARE_45]: '45° Square',
  [GRID_PATTERN_IDS.HEX]: 'Hex',
  [GRID_PATTERN_IDS.HEX_90]: '90° Hex',
  [GRID_PATTERN_IDS.HEX_SPACED]: 'Spaced Hex',
  [GRID_PATTERN_IDS.FREEFORM]: 'Freeform',
  [GRID_PATTERN_IDS.THREE_D]: '3D',
}

const PATTERN_INFO: Record<string, string> = {
  [GRID_PATTERN_IDS.SQUARE]: 'Only Compatible with Lg Square, Square, Quarter, Pixel, Lg Halfflap, Halfflap, Shiplap, Mini Shiplap, 90° Triangle, Sm 90° Triangle, Lg Half Circle, Quarter Circle, Half Circle, Sm Half Circle',
  [GRID_PATTERN_IDS.SQUARE_45]: 'Only Compatible with Lg Square, Square, Quarter, Pixel, Lg Halfflap, Halfflap, Shiplap, Mini Shiplap, 90° Triangle, Sm 90° Triangle, Lg Half Circle, Quarter Circle, Half Circle, Sm Half Circle',
  [GRID_PATTERN_IDS.HEX]: 'Only Compatible with Hex, Lg Diamond, Diamond, Lg Triangle, and Sm Triangle',
  [GRID_PATTERN_IDS.HEX_90]: 'Only Compatible with Hex, Lg Diamond, Diamond, Lg Triangle',
  [GRID_PATTERN_IDS.HEX_SPACED]: 'Only Compatible with Hex, Lg Diamond, Diamond',
  [GRID_PATTERN_IDS.FREEFORM]: 'Compatible with all shapes',
  [GRID_PATTERN_IDS.THREE_D]: 'Only Compatible with 3D blocks',
}

const props = withDefaults(defineProps<{ open: boolean; triggerEl?: HTMLElement | null; highlightedGridIds?: string[] }>(), { highlightedGridIds: () => [] })
const emit = defineEmits<{ close: [] }>()

const gridStore = useGridStore()
const projectStore = useProjectStore()
const runtimeConfig = useRuntimeConfig()

const popoverEl = ref<HTMLElement | null>(null)
const scrollEl = shallowRef<HTMLElement | null>(null)
const colorInput = ref<HTMLInputElement | null>(null)

// --- Pattern display order (matches Figma) ---
const PATTERN_ORDER = [
  GRID_PATTERN_IDS.SQUARE,
  GRID_PATTERN_IDS.SQUARE_45,
  GRID_PATTERN_IDS.HEX,
  GRID_PATTERN_IDS.HEX_90,
  GRID_PATTERN_IDS.HEX_SPACED,
  GRID_PATTERN_IDS.FREEFORM,
  GRID_PATTERN_IDS.THREE_D,
]
const orderedPatterns = computed(() =>
  PATTERN_ORDER.map(id => GRID_PATTERN_LIST.find(p => p.id === id)!).filter(Boolean)
)

// --- Dynamic max-height and scrolling ---
const popoverMaxHeight = ref<string | null>(null)
let canvasResizeObserver: ResizeObserver | null = null

const updateMaxHeight = () => {
  if (!popoverEl.value) return
  const popoverRect = popoverEl.value.getBoundingClientRect()
  const canvasEl = popoverEl.value.closest('.new-studio__canvas') as HTMLElement | null
  const workspaceEl = popoverEl.value.closest('.new-studio__workspace') as HTMLElement | null
  const bottomLimit = Math.min(
    window.innerHeight,
    canvasEl ? canvasEl.getBoundingClientRect().bottom : window.innerHeight,
    workspaceEl ? workspaceEl.getBoundingClientRect().bottom : window.innerHeight,
  )
  // Maintain 16px buffer from the bottom of the visible area
  const available = bottomLimit - popoverRect.top - 16
  popoverMaxHeight.value = `${Math.max(0, Math.floor(available))}px`
  nextTick(updateTooltipPosition)
}

const observeBounds = () => {
  if (typeof ResizeObserver === 'undefined' || !popoverEl.value || canvasResizeObserver) return
  canvasResizeObserver = new ResizeObserver(updateMaxHeight)
  for (const selector of ['.new-studio__canvas', '.new-studio__workspace']) {
    const el = popoverEl.value.closest(selector)
    if (el) canvasResizeObserver.observe(el)
  }
}

// --- Tooltip tracking (teleported to body so it never clips on scroll) ---
const hoveredPattern = ref<GridPattern | null>(null)
const hoveredPatternEl = ref<HTMLElement | null>(null)
const tooltipPos = ref<{ top: number; left: number } | null>(null)

const updateTooltipPosition = () => {
  if (!hoveredPatternEl.value || !scrollEl.value) {
    tooltipPos.value = null
    return
  }
  const pr = scrollEl.value.getBoundingClientRect()
  const r = hoveredPatternEl.value.getBoundingClientRect()
  // Hide tooltip if the hovered pattern button scrolled outside the popover viewport
  if (r.bottom < pr.top || r.top > pr.bottom) {
    tooltipPos.value = null
    return
  }
  tooltipPos.value = {
    top: Math.round(r.top + r.height / 2),
    left: Math.round(r.right + 8),
  }
}

const showTooltip = (pattern: GridPattern, e: MouseEvent) => {
  hoveredPattern.value = pattern
  hoveredPatternEl.value = e.currentTarget as HTMLElement
  updateTooltipPosition()
}

const hideTooltip = () => {
  hoveredPattern.value = null
  hoveredPatternEl.value = null
  tooltipPos.value = null
}

let returnFocusEl: HTMLElement | null = null

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    returnFocusEl = document.activeElement instanceof HTMLElement ? document.activeElement : null
    nextTick(() => {
      updateMaxHeight()
      observeBounds()
      popoverEl.value?.focus()
    })
  } else {
    if (popoverEl.value?.contains(document.activeElement)) returnFocusEl?.focus()
    returnFocusEl = null
    hideTooltip()
    canvasResizeObserver?.disconnect()
    canvasResizeObserver = null
  }
})

watch([() => props.open, () => props.highlightedGridIds], async ([isOpen]) => {
  if (!isOpen || !props.highlightedGridIds.length) return
  await nextTick()
  scrollEl.value?.querySelector<HTMLElement>('.grid-options-popover__pattern--highlighted')
    ?.scrollIntoView?.({ block: 'nearest' })
}, { flush: 'post' })

// --- Grid Size ---
const widthFt = ref(gridStore.gridSize.width.ft)
const widthIn = ref(Math.round(gridStore.gridSize.width.in))
const heightFt = ref(gridStore.gridSize.height.ft)
const heightIn = ref(Math.round(gridStore.gridSize.height.in))

let sizeDebounce: ReturnType<typeof setTimeout> | null = null

watch([widthFt, widthIn, heightFt, heightIn], () => {
  if (sizeDebounce) clearTimeout(sizeDebounce)
  sizeDebounce = setTimeout(() => {
    const w = (widthFt.value || 0) * FOOT_IN_PIXELS + (widthIn.value || 0) * INCH_IN_PIXELS
    const h = (heightFt.value || 0) * FOOT_IN_PIXELS + (heightIn.value || 0) * INCH_IN_PIXELS
    if (w > 0 && h > 0) {
      gridStore.setGridSize(w, h)
      if (runtimeConfig.public.autoSaveOnChange) projectStore.save(true, true)
    }
  }, 400)
})

// Sync from store if it changes externally
watch(() => gridStore.gridSize, (size) => {
  widthFt.value = size.width.ft
  widthIn.value = Math.round(size.width.in)
  heightFt.value = size.height.ft
  heightIn.value = Math.round(size.height.in)
})

// --- Background Color ---
const bgColor = ref(projectStore.bgColor || gridStore.gridBgColor || 'white')

const handleBgChange = () => {
  gridStore.setBgColor(bgColor.value)
  projectStore.bgColor = bgColor.value
  if (runtimeConfig.public.autoSaveOnChange) projectStore.save()
}

const handleBgColorPick = () => {
  // Coloris updates the bound input value — read it back
  if (colorInput.value) {
    bgColor.value = colorInput.value.value
  }
  gridStore.setBgColor(bgColor.value)
  projectStore.bgColor = bgColor.value
  if (runtimeConfig.public.autoSaveOnChange) projectStore.save()
}

// Sync from store
watch(() => gridStore.gridBgColor, (color) => {
  bgColor.value = color
})

// --- Grid Type ---
const currentPatternId = computed(() => gridStore.gridPattern?.id)
const pendingPattern = ref<GridPattern | null>(null)

const selectPattern = (pattern: GridPattern) => {
  if (pattern.id === currentPatternId.value) return
  pendingPattern.value = pattern
}

const confirmPatternChange = () => {
  if (!pendingPattern.value) return
  const pattern = pendingPattern.value
  const defaultShape = GRID_MENU[pattern.id]?.default
  if (!defaultShape) return

  const gridRef = projectStore.gridRef
  applyGridPatternChange(gridStore, pattern, { preserveDimensions: true })
  projectStore.$reset()
  projectStore.setGridRef(gridRef)
  useRouter().replace({ query: {} })
  deleteCookie('studio_design_id')
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
  gridStore.setSelectedShape(defaultShape)
  gridStore.setTempItemByCurrentSelection()

  pendingPattern.value = null
  emit('close')
}

// --- Dismiss on click outside ---
const onPointerDown = (e: PointerEvent) => {
  if (!popoverEl.value) return
  const target = e.target as Node
  if (popoverEl.value.contains(target)) return
  if (props.triggerEl?.contains(target)) return
  if ((target as Element)?.closest?.('#clr-picker')) return
  emit('close')
}

// --- Coloris setup ---
let colorisCleanup: (() => void) | null = null

onMounted(async () => {
  document.addEventListener('pointerdown', onPointerDown)
  window.addEventListener('resize', updateMaxHeight)

  if (props.open) {
    nextTick(() => {
      updateMaxHeight()
      observeBounds()
    })
  }

  // Coloris is a singleton — safe to re-init; it just applies the config
  await import('@melloware/coloris/dist/coloris.css')
  const Coloris = (await import('@melloware/coloris')).default
  Coloris.init()
  Coloris({
    themeMode: 'light',
    alpha: false,
    format: 'hex',
  } as any)

  // Re-init after DOM is ready so Coloris finds the [data-coloris] input
  await nextTick()
  Coloris.init()

  document.addEventListener('coloris:pick', handleBgColorPick)
  colorisCleanup = () => {
    document.removeEventListener('coloris:pick', handleBgColorPick)
    Coloris.close()
  }

  // Focus the popover on open for keyboard accessibility
  nextTick(() => popoverEl.value?.focus())
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onPointerDown)
  window.removeEventListener('resize', updateMaxHeight)
  canvasResizeObserver?.disconnect()
  canvasResizeObserver = null
  hideTooltip()
  colorisCleanup?.()
  if (sizeDebounce) clearTimeout(sizeDebounce)
})
</script>

<style scoped>
.grid-options-popover {
  position: absolute;
  z-index: 100;
  top: 0;
  left: calc(100% + 12px);
  width: 320px;
  max-height: calc(100vh - 250px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid var(--studio-border);
  border-radius: 12px;
  background: var(--studio-surface);
  box-shadow: var(--studio-card-shadow);
  pointer-events: auto;
  outline: none;
}

.grid-options-popover__scroll {
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-padding-block: 38px;
  scrollbar-width: thin;
  scrollbar-color: #ccd5da transparent;
}

.grid-options-popover__scroll::-webkit-scrollbar {
  width: 6px;
}

.grid-options-popover__scroll::-webkit-scrollbar-track {
  background: transparent;
  margin: 6px 0;
}

.grid-options-popover__scroll::-webkit-scrollbar-thumb {
  background-color: #ccd5da;
  border-radius: 3px;
}

.grid-options-popover__scroll::-webkit-scrollbar-thumb:hover {
  background-color: #b8c6ce;
}

.grid-options-popover__section {
  padding: 16px 18px 15px;
  border-bottom: 1px solid #f0efeb;
}

.grid-options-popover__section:last-child {
  border-bottom: none;
}

.grid-options-popover__section--types {
  padding-bottom: 12px;
}

.grid-options-popover__heading {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #9a9994;
}

/* Grid Size */
.grid-options-popover__size-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.grid-options-popover__heading--size {
  margin: 0;
  white-space: nowrap;
}

.grid-options-popover__size {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.grid-options-popover__size-row {
  display: flex;
  align-items: center;
  gap: 5px;
}

.grid-options-popover__size-label {
  width: 39px;
  font-size: 11px;
  font-weight: var(--studio-font-semibold);
  color: #8c8b86;
}

.grid-options-popover__input {
  width: 46px;
  height: 21px;
  padding: 4px;
  border: 1px solid #e4e3de;
  border-radius: 2px;
  font-family: var(--studio-font-family);
  font-size: 11px;
  font-weight: 400;
  text-align: center;
  color: #000;
  background: #fff;
}

.grid-options-popover__input:focus {
  outline: 2px solid var(--studio-focus-ring);
  outline-offset: 1px;
}

.grid-options-popover__unit {
  font-size: 11px;
  font-weight: var(--studio-font-semibold);
  color: #8c8b86;
}

/* Background Color */
.grid-options-popover__color-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.grid-options-popover__color-row .grid-options-popover__heading {
  margin: 0;
}

.grid-options-popover__color-swatch-wrap {
  display: flex;
  align-items: center;
}

.grid-options-popover__color-swatch-wrap {
  position: relative;
  width: 36px;
  height: 24px;
}

.grid-options-popover__color-input {
  width: 36px;
  height: 24px;
  padding: 0;
  border: 1px solid var(--studio-border);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0;
  color: transparent;
  transition: border-color 0.15s;
}

.grid-options-popover__color-input:hover {
  border-color: var(--studio-text-muted);
}

/* Grid Type */
.grid-options-popover__pattern-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.grid-options-popover__pattern {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  cursor: pointer;
  transition: background-color 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.grid-options-popover__pattern:hover {
  background: #dfe4e5;
}

.grid-options-popover__pattern--active {
  background: #ccd5da;
  border: 2px solid #b8c6ce;
}

.grid-options-popover__pattern--active:hover {
  background: #ccd5da;
}

.grid-options-popover__pattern::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 0 2px var(--studio-text);
  pointer-events: none;
  opacity: 0;
  transform: scale(0.985);
  transition: opacity 200ms ease-out, transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}

.grid-options-popover__pattern--highlighted::after {
  opacity: 1;
  transform: scale(1);
}

.grid-options-popover__pattern--highlighted {
  background: var(--studio-canvas-surface);
}

.grid-options-popover__pattern--highlighted:hover {
  background: var(--studio-surface);
}

.grid-options-popover__available {
  margin-left: auto;
  color: var(--studio-text);
  font-size: 11px;
  font-weight: 700;
}

.grid-options-popover__pattern-img {
  width: 42px;
  height: 42px;
  border: 1px solid var(--studio-border);
  border-radius: 6px;
  object-fit: cover;
  background: #f8f7f4;
}

.grid-options-popover__pattern-label {
  font-size: 15px;
  font-weight: var(--studio-font-bold);
  color: var(--studio-text-dark);
}

.grid-availability-enter-active,
.grid-availability-leave-active {
  transition: opacity 180ms ease-out, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.grid-availability-enter-from,
.grid-availability-leave-to {
  opacity: 0;
  transform: translateX(-3px);
}

@media (prefers-reduced-motion: reduce) {
  .grid-options-popover__pattern,
  .grid-options-popover__pattern::after,
  .grid-availability-enter-active,
  .grid-availability-leave-active {
    transition: none;
    transform: none;
  }
}

/* Info bubble tooltip (teleported to body) */
.grid-options-popover__pattern-tooltip {
  position: fixed;
  z-index: 9999;
  transform: translateY(-50%);
  width: 240px;
  padding: 14px 6px;
  background: #e6e5e0;
  border: 1px solid #b4b3ad;
  box-shadow: 0 3px 5px rgb(20 20 22 / 0.1);
  font-family: var(--studio-font-family);
  font-size: 10px;
  font-weight: 400;
  line-height: 15px;
  text-align: center;
  color: #454540;
  pointer-events: none;
  animation: tooltip-fade-in 0.15s ease;
}

@keyframes tooltip-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Warning Modal Overlay */
.grid-warning-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 0.5);
}

.grid-warning-modal {
  width: 380px;
  border-radius: 12px;
  background: var(--studio-surface, #fff);
  box-shadow: 0 20px 60px rgb(0 0 0 / 0.25);
}

.grid-warning-modal__content {
  padding: 28px 24px 24px;
  border-radius: 12px;
  background: var(--studio-surface, #fff);
  border: 1px solid var(--studio-border, #e6e5e0);
}

.grid-warning-modal__content p {
  margin: 0 0 18px;
  font-family: var(--studio-font-family, Montserrat, sans-serif);
  font-size: 15px;
  font-weight: var(--studio-font-semibold, 600);
  line-height: 1.5;
  color: var(--studio-text, #454540);
  text-align: center;
}

.grid-warning-modal__actions {
  display: flex;
  gap: 10px;
}

.grid-warning-modal__cancel {
  flex: 1;
  min-height: 42px;
  border: 1px solid var(--studio-border, #e6e5e0);
  border-radius: 8px;
  background: var(--studio-surface, #fff);
  font: inherit;
  font-size: 14px;
  font-weight: var(--studio-font-semibold, 600);
  color: var(--studio-text, #454540);
  cursor: pointer;
}

.grid-warning-modal__cancel:hover {
  background: #f8f7f4;
}

.grid-warning-modal__confirm {
  flex: 1;
  min-height: 42px;
  border: none;
  border-radius: 8px;
  background: #2e3a46;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: var(--studio-font-bold, 700);
  cursor: pointer;
}

.grid-warning-modal__confirm:hover {
  background: #3a4a56;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animation */
@media (prefers-reduced-motion: no-preference) {
  .grid-options-popover {
    animation: popover-in 150ms cubic-bezier(.22, 1, .36, 1);
  }
}

@keyframes popover-in {
  from { opacity: 0; transform: translateX(-4px); }
}
</style>
